import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { SPOTIFY_OAUTH_STATE_KEY } from '../services/api';

// Spotify's account-connect callback (COL-330). Unlike Callback.jsx (Cognito's
// own OAuth callback), this is not a login -- the user is already
// authenticated when this page loads (it's wrapped in ProtectedRoute), and
// `state` here is a plain CSRF nonce with no return-path semantics. Does not
// touch AuthContext's Cognito state machinery or its `collections_code_verifier`
// sessionStorage key.
export default function SpotifyCallback() {
  const navigate = useNavigate();
  const [error, setError] = useState(null);

  useEffect(() => {
    async function processCallback() {
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      const state = params.get('state');
      const oauthError = params.get('error');

      // Read + clear the stored state up front, on every path, so a stale
      // value can never be reused by a later (e.g. replayed) callback.
      const storedState = sessionStorage.getItem(SPOTIFY_OAUTH_STATE_KEY);
      sessionStorage.removeItem(SPOTIFY_OAUTH_STATE_KEY);

      if (oauthError) {
        setError(`Spotify sign-in failed: ${oauthError}`);
        return;
      }

      if (!code) {
        setError('No authorization code received');
        return;
      }

      // A missing stored value (e.g. a stale/replayed callback URL) must be
      // treated as a failure, never silently accepted.
      if (!storedState || storedState !== state) {
        setError('Spotify sign-in failed: invalid state');
        return;
      }

      try {
        await api.connectSpotify(code);
        navigate('/settings', { replace: true, state: { spotifyConnected: true } });
      } catch (e) {
        navigate('/settings', { replace: true, state: { spotifyConnectFailed: true } });
      }
    }

    processCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) {
    return (
      <div className="callback-error">
        <div style={{
          background: 'var(--danger)',
          color: 'var(--status-contrast)',
          padding: 'var(--space-md) var(--space-lg)',
          borderRadius: 'var(--radius-md)',
        }}>
          {error}
        </div>
        <a href="/settings" className="btn btn-secondary">
          Return to settings
        </a>
      </div>
    );
  }

  return (
    <div className="loading-overlay">
      <div style={{ textAlign: 'center' }}>
        <div className="loading-spinner" style={{ margin: '0 auto var(--space-lg)' }} />
        <p style={{ color: 'var(--text-muted)' }}>Connecting Spotify...</p>
      </div>
    </div>
  );
}
